import React from 'react';
import { Info, Droplet, BarChart2, DollarSign, Car, Leaf, Thermometer } from 'lucide-react';

/**
 * Компонент с информацией о расходе топлива в России и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о расходе топлива: факторы, нормы и экономия
      </h2>
      
      <div className="prose prose-blue dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Car className="w-5 h-5 text-blue-500" />
          Факторы, влияющие на расход топлива
        </h3>
        <p>
          <strong>Расход топлива</strong> — это количество топлива, потребляемое автомобилем на определенное расстояние (обычно измеряется в литрах на 100 километров). На расход топлива влияет множество факторов:
        </p>

        <ul>
          <li><strong>Технические характеристики автомобиля:</strong> тип и объем двигателя, мощность, возраст, трансмиссия, аэродинамика, масса.</li>
          <li><strong>Стиль вождения:</strong> резкие ускорения и торможения значительно увеличивают расход.</li>
          <li><strong>Дорожные условия:</strong> тип дороги, её качество, рельеф местности, погодные условия.</li>
          <li><strong>Нагрузка:</strong> масса пассажиров и груза, использование багажника на крыше.</li>
          <li><strong>Техническое состояние:</strong> давление в шинах, состояние двигателя, качество масла и фильтров.</li>
          <li><strong>Использование вспомогательных систем:</strong> кондиционер, обогрев, электрические устройства.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Droplet className="w-5 h-5 text-blue-500" />
          Нормы расхода топлива в России
        </h3>
        
        <p>
          В России существуют <strong>нормативы расхода топлива</strong>, установленные Министерством транспорта. Эти нормативы используются для учета расходов на служебные автомобили. Однако реальный расход часто отличается от нормативов. Средние показатели расхода топлива для разных классов автомобилей в России:
        </p>
        
        <div className="not-prose my-6 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Класс автомобиля</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Город (л/100км)</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Трасса (л/100км)</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Смешанный (л/100км)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-700">
              <tr>
                <td className="px-4 py-2 text-sm text-gray-900 dark:text-white font-medium">Малолитражные (до 1.6 л)</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">7-9</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">5-6</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">6-7</td>
              </tr>
              <tr className="bg-gray-50 dark:bg-gray-800">
                <td className="px-4 py-2 text-sm text-gray-900 dark:text-white font-medium">Компактные (1.6-2.0 л)</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">8-12</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">6-8</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">7-9</td>
              </tr>
              <tr>
                <td className="px-4 py-2 text-sm text-gray-900 dark:text-white font-medium">Средний класс (2.0-3.0 л)</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">11-14</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">7-10</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">9-12</td>
              </tr>
              <tr className="bg-gray-50 dark:bg-gray-800">
                <td className="px-4 py-2 text-sm text-gray-900 dark:text-white font-medium">Премиум/Внедорожники (меньше 3.0 л)</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">14-20</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">10-14</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">12-17</td>
              </tr>
              <tr>
                <td className="px-4 py-2 text-sm text-gray-900 dark:text-white font-medium">Дизельные автомобили</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">6-10</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">5-8</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">5.5-9</td>
              </tr>
              <tr className="bg-gray-50 dark:bg-gray-800">
                <td className="px-4 py-2 text-sm text-gray-900 dark:text-white font-medium">Гибриды</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">5-7</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">4-6</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">4.5-6.5</td>
              </tr>
              <tr>
                <td className="px-4 py-2 text-sm text-gray-900 dark:text-white font-medium">Автомобили на газе (пропан)</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">9-12</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">8-10</td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">8.5-11</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2">
            <Thermometer className="w-4 h-4" />
            Влияние климата России на расход топлива
          </h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Холодный климат большей части России значительно влияет на расход топлива, особенно зимой. 
            При температуре ниже -20°C расход может увеличиваться на 15-30% из-за прогрева, повышенной 
            вязкости технических жидкостей и снижения КПД батареи. Использование предпускового подогревателя 
            помогает снизить этот эффект.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-blue-500" />
          Стоимость эксплуатации и текущие цены
        </h3>
        
        <p>
          Стоимость топлива в России различается в зависимости от региона. По состоянию на начало 2025 года, средние цены на топливо составляют:
        </p>
        
        <ul>
          <li><strong>АИ-92:</strong> 51-53 ₽/л</li>
          <li><strong>АИ-95:</strong> 54-57 ₽/л</li>
          <li><strong>АИ-98:</strong> 60-63 ₽/л</li>
          <li><strong>Дизельное топливо:</strong> 57-60 ₽/л</li>
          <li><strong>Газ (пропан):</strong> 24-26 ₽/л</li>
          <li><strong>Газ (метан):</strong> 22-24 ₽/м³</li>
          <li><strong>Электричество (для электромобилей):</strong> 4-6 ₽/кВт·ч (зависит от тарифа и региона)</li>
        </ul>
        
        <p>
          При оценке полной стоимости эксплуатации автомобиля, помимо затрат на топливо, необходимо учитывать:
        </p>
        
        <ul>
          <li><strong>Техническое обслуживание:</strong> от 15,000 до 50,000 ₽ в год (зависит от класса и марки автомобиля)</li>
          <li><strong>Страхование:</strong> ОСАГО от 5,000 до 15,000 ₽, КАСКО от 30,000 до 150,000 ₽ в год</li>
          <li><strong>Транспортный налог:</strong> зависит от мощности двигателя и региона, в среднем от 2,000 до 15,000 ₽ в год</li>
          <li><strong>Парковка:</strong> в Москве и Санкт-Петербурге от 3,000 до 8,000 ₽ в месяц</li>
          <li><strong>Плата за проезд по платным дорогам:</strong> зависит от маршрутов, на трассе М4 "Дон" примерно 2,000 ₽ в одну сторону (Москва-Краснодар)</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-blue-500" />
          Как снизить расход топлива
        </h3>
        
        <p>Снижение расхода топлива позволяет не только экономить деньги, но и уменьшить негативное воздействие на окружающую среду. Рекомендации по экономии топлива:</p>
        
        <ol>
          <li><strong>Регулярное техническое обслуживание:</strong> замена масла, фильтров, свечей зажигания, проверка состояния топливной системы.</li>
          <li><strong>Контроль давления в шинах:</strong> недокачанные шины могут увеличить расход топлива на 3-5%.</li>
          <li><strong>Плавное вождение:</strong> избегайте резких ускорений и торможений. Поддерживайте постоянную скорость, используйте круиз-контроль на трассе.</li>
          <li><strong>Оптимальная скорость:</strong> наибольшая экономичность достигается при скорости 60-90 км/ч. На высоких скоростях расход резко возрастает.</li>
          <li><strong>Снижение веса:</strong> удалите из автомобиля лишний груз, особенно из багажника.</li>
          <li><strong>Рациональное использование кондиционера:</strong> на низких скоростях открытые окна экономичнее, на высоких - кондиционер с закрытыми окнами.</li>
          <li><strong>Выбор маршрута:</strong> избегайте пробок и участков с частыми остановками, предпочитайте маршруты с меньшим количеством светофоров.</li>
          <li><strong>Эффективное переключение передач:</strong> переключайте передачи при оптимальных оборотах двигателя (для бензиновых автомобилей 2000-2500 об/мин).</li>
        </ol>

        <h3 className="flex items-center gap-2">
          <Leaf className="w-5 h-5 text-green-500" />
          Экологический аспект
        </h3>
        
        <p>
          Расход топлива напрямую связан с экологическим воздействием автомобиля. Чем больше расход, 
          тем выше выбросы CO₂ и других вредных веществ. В среднем, при сгорании:
        </p>
        
        <ul>
          <li>1 литр бензина образуется около 2,3 кг CO₂</li>
          <li>1 литр дизельного топлива - около 2,7 кг CO₂</li>
          <li>1 литр сжиженного газа (пропан) - около 1,6 кг CO₂</li>
          <li>1 м³ метана - около 1,4 кг CO₂</li>
        </ul>
        
        <p>
          В России постепенно ужесточаются экологические требования к автомобилям. С 2022 года действует 
          стандарт Евро-5, ограничивающий выбросы вредных веществ. При выборе автомобиля стоит обращать внимание 
          на его экологический класс и показатели выбросов CO₂.
        </p>
        
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800 my-6">
          <h4 className="text-green-700 dark:text-green-300 font-medium flex items-center gap-2">
            <Leaf className="w-4 h-4" />
            Альтернативный транспорт в России
          </h4>
          <p className="text-green-600 dark:text-green-400 text-sm">
            В крупных городах России развивается инфраструктура для альтернативного транспорта. 
            Электромобили, хотя и медленно, но начинают появляться на дорогах. В Москве, Санкт-Петербурге 
            и других крупных городах действуют программы развития общественного электротранспорта, а также 
            строятся зарядные станции для электромобилей.
          </p>
        </div>

        <h3>Заключение</h3>
        
        <p>
          Наш калькулятор расхода топлива поможет вам спланировать расходы на поездки, оценить эффективность 
          вашего автомобиля и найти способы экономии. Помните, что снижение расхода топлива — это не только 
          экономическая выгода, но и вклад в защиту окружающей среды.
        </p>
        
        <p>
          Для получения наиболее точных результатов рекомендуем регулярно отслеживать фактический расход 
          вашего автомобиля в различных условиях и использовать эти данные при расчетах. Также учитывайте 
          сезонные изменения в расходе топлива, особенно в холодное время года.
        </p>
      </div>
    </article>
  );
};

export default InfoContent;